import { useEffect, useRef, useState } from "react";
import DatePickerComp from "react-datepicker";
import Calendar from "../../icons/Calendar";
import "react-datepicker/dist/react-datepicker.css";
import { IoCloseCircle } from "react-icons/io5";

// Regular expression to match the expected date format
const dateFormatRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;
const DatePicker = ({
  placeholderText,
  className,
  startDate,
  onDateChange,
  error,
  disabled,
  handleClear,
  minDate,
  height,
  title,
}) => {
  const [currentDateFormat, setCurrentDateFormat] = useState("dd MMM yyyy");
  const [date, setDate] = useState("");
  const defaultHeight = "54px";
  const dynamicHeight = height ? height : defaultHeight;
  useEffect(() => {
    // Check if startDate is a valid date format
    if (startDate && !isNaN(new Date(startDate))) {
      setDate(startDate);
    } else if (startDate === "") {
      setDate("");
    }
  }, [startDate]);

  const datePickerRef = useRef(null);

  /** Calendar Icon Click */
  const handleCalendarClick = (e) => {
    e?.stopPropagation();
    const isOpen = datePickerRef.current.isCalendarOpen();
    if (!disabled && !isOpen) {
      datePickerRef.current.setOpen(!isOpen);
    }
  };

  /** Mobile touch handling */
  const handleTouchStart = (e) => {
    e?.stopPropagation();
  };

  const handleTouchEnd = (e) => {
    e?.stopPropagation();
    const isOpen = datePickerRef.current.isCalendarOpen();
    if (!disabled && !isOpen) {
      datePickerRef.current.setOpen(!isOpen);
    }
  };

  const handleDateChange = (date) => {
    handleCalendarClick();
    onDateChange && onDateChange(date);
    setDate(date);
  };

  const errorElementRef = useRef(null);

  // On error scrollIntoView
  useEffect(() => {
    if (error && errorElementRef.current) {
      errorElementRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [error]);

  /** Parse input string to Date */
  const parseInputToDate = (value) => {
    const [day, month, year] = value.split("-").map(Number);
    const parsedDate = new Date(year, month - 1, day); // JavaScript Date uses 0-based months
    return !isNaN(parsedDate.getTime()) ? parsedDate : null;
  };

  // Define Popper modifiers to control z-index and positioning
  const popperModifiers = [
    {
      name: "offset",
      options: {
        offset: [0, 5],
      },
    },
    {
      name: "preventOverflow",
      options: {
        boundary: "viewport",
        padding: 10,
      },
    },
    {
      name: "zIndex",
      enabled: true,
      phase: "write",
      fn({ state }) {
        state.styles.popper.zIndex = 99999999999; // High z-index to ensure it's on top
        return state;
      },
    },
  ];

  return (
    <div ref={error ? errorElementRef : null} className="">
      {title && (
        <label className="text-darkgrey text-bodyRB mb-[2px]">{title}</label>
      )}
      {/* Date picker Section */}
      <div
        className={`w-full h-[${dynamicHeight}] rounded-15  bg-white shadow-card flex items-center justify-between pl-3 pr-3 ${className}
        ${error ? "border-[1.5px] border-danger" : "border border-[#B9B9B9]"}
        `}
      >
        <div className="">
          <DatePickerComp
            wrapperClassName="datePicker"
            ref={datePickerRef}
            className="border-none text-bodyRB cursor-pointer  w-full outline-0 text-left mt-[2px] select-none bg-transparent"
            selected={date instanceof Date ? date : null}
            onChange={(date) => {
              handleDateChange(date);
            }}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            minDate={minDate ? minDate : undefined}
            dateFormat={currentDateFormat}
            //  dateFormat="dd MMM yyyy"
            placeholderText={placeholderText || "DD-MM-YYYY"}
            peekNextMonth
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
            autoComplete="off"
            disabled={disabled}
            onInputChange={(value) => {
              const parsedDate = parseInputToDate(value);
              if (parsedDate) {
                setDate(parsedDate); // Update only if the date is valid
              }
            }}
            onFocus={() => {
              // Change date format on focus
              setCurrentDateFormat("dd-MM-yyyy");
            }}
            onCalendarClose={() => {
              // Reset date format on CalendarClose
              setCurrentDateFormat("dd MMM yyyy");
            }}
            onBlur={() => {
              // Reset date format on blur
              setCurrentDateFormat("dd MMM yyyy");
            }}
            // Added for proper positioning
            popperPlacement="bottom-start"
            popperModifiers={popperModifiers}
            portalId="calendar-portal"
            popperContainer={({ children }) => {
              return (
                <div
                  className="calendar-popper-container"
                  style={{ zIndex: 9999 }}
                >
                  {children}
                </div>
              );
            }}
            // Force calendar to appear above other elements
            usePopper={true}
            // Mobile-specific props
            showPopperArrow={false}
            enableTabLoop={false}
            autoFocus={false}
          />
        </div>
        <div className="cursor-pointer flex items-center gap-1">
          <div
            onClick={handleCalendarClick}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <Calendar className={""} />
          </div>
          {handleClear && !disabled && (
            <div
              className="w-5 text-xl text-gray-500 mt-1"
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
            >
              <IoCloseCircle />
            </div>
          )}
        </div>
      </div>
      {error && (
        <p className="text-danger text-smallLB mx-2 my-0.5 -mb-5 absolute">
          {error}
        </p>
      )}
      {/* Date picker Section End */}
    </div>
  );
};

export default DatePicker;
